import React, { useEffect, useState } from "react";
import { Typography } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { useAppTheme } from "context/themeContext";
import { updateCompany } from "services/api";
import { useAuth } from "context/authContext";
import { getSafeColor } from "services/utils";
import { useTranslation } from "react-i18next";

function ColorPicker() {
  const {
    primaryColor,
    setPrimaryColor,
    navBarColor,
    setNavBarColor,
    sideNavColor,
    setSideNavColor,
    backgroundColor,
    setBackgroundColor,
    navBarTextColor,
    setNavBarTextColor,
    topNavBarTextColor,
    setTopNavBarTextColor,
    DEFAULT_PRIMARY,
    DEFAULT_NAVBAR,
    DEFAULT_SIDENAV,
    DEFAULT_BACKGROUND,
    DEFAULT_NAVBAR_TEXT,
    DEFAULT_TOP_NAVBAR_TEXT_COLOR,
  } = useAppTheme();

  const { companies } = useAuth(); // Ensure updateCompany is destructured
  const company = companies?.[0];
  const { t, i18n } = useTranslation();

  const [tempColors, setTempColors] = useState({
    primary: primaryColor,
    navBar: navBarColor,
    sideNav: sideNavColor,
    backgroundColor: backgroundColor,
    navBarTextColor: navBarTextColor,
    topNavBarTextColor: topNavBarTextColor,
  });

  // 🔄 THE FIX: Sync local state when the Global Theme or Company Data changes
  useEffect(() => {
    if (company) {
      setTempColors({
        primary: primaryColor,
        navBar: navBarColor || DEFAULT_NAVBAR,
        sideNav: sideNavColor || DEFAULT_SIDENAV,
        backgroundColor: backgroundColor || DEFAULT_BACKGROUND,
        navBarTextColor: navBarTextColor || DEFAULT_NAVBAR_TEXT,
        topNavBarTextColor: topNavBarTextColor || DEFAULT_TOP_NAVBAR_TEXT_COLOR,
      });
    }
  }, [
    primaryColor,
    navBarColor,
    sideNavColor,
    backgroundColor,
    navBarTextColor,
    topNavBarTextColor,
    company,
  ]);

  const handleChange = (key, value) => {
    setTempColors((prev) => ({ ...prev, [key]: value }));
  };

  const handleApply = async () => {
    // 1. Update Global Context immediately (Optimistic UI)
    setPrimaryColor(tempColors.primary);
    setNavBarColor(tempColors.navBar);
    setSideNavColor(tempColors.sideNav);
    setBackgroundColor(tempColors.backgroundColor);
    setNavBarTextColor(tempColors.navBarTextColor);
    setTopNavBarTextColor(tempColors.topNavBarTextColor);
    // 2. Save to Database
    try {
      await updateCompany({
        ...company,
        primaryColor: tempColors.primary,
        navBarColor: tempColors.navBar,
        sideNavColor: tempColors.sideNav,
        backgroundColor: tempColors.backgroundColor,
        textColor: tempColors.navBarTextColor,
        topNavBarTextColor: tempColors.topNavBarTextColor,
      });
    } catch (error) {
      console.error("Failed to update colors", error);
      // Optional: Rollback UI if DB fails
    }
  };

  // Reset all colors to default
  const handleReset = () => {
    const resetColors = {
      primary: DEFAULT_PRIMARY,
      navBar: DEFAULT_NAVBAR,
      sideNav: DEFAULT_SIDENAV,
      backgroundColor: DEFAULT_BACKGROUND,
      navBarTextColor: DEFAULT_NAVBAR_TEXT, // 🆕
      topNavBarTextColor: DEFAULT_TOP_NAVBAR_TEXT_COLOR,
    };
    setTempColors(resetColors);
    setPrimaryColor(DEFAULT_PRIMARY);
    setNavBarColor(DEFAULT_NAVBAR);
    setSideNavColor(DEFAULT_SIDENAV);
    setBackgroundColor(DEFAULT_BACKGROUND);
    setNavBarTextColor(DEFAULT_NAVBAR_TEXT); // 🆕
    setTopNavBarTextColor(DEFAULT_TOP_NAVBAR_TEXT_COLOR);
    updateCompany({
      ...company,
      primaryColor: DEFAULT_PRIMARY,
      navBarColor: DEFAULT_NAVBAR,
      sideNavColor: DEFAULT_SIDENAV,
      backgroundColor: DEFAULT_BACKGROUND,
      textcolor: DEFAULT_NAVBAR_TEXT, // 🆕
      topNavBarTextColor: DEFAULT_TOP_NAVBAR_TEXT_COLOR,
    });
  };

  // Reusable color input
  const renderColorInput = (label, key, colorValue) => (
    <MDBox mb={1} display="flex" alignItems="center" gap={1}>
      <input
        type="color"
        value={colorValue === "transparent" ? "#ffffff" : colorValue}
        onChange={(e) => handleChange(key, e.target.value)}
        disabled={colorValue === "transparent"}
        style={{
          width: 40,
          height: 40,
          border: "none",
          borderRadius: "50%",
          cursor: colorValue === "transparent" ? "not-allowed" : "pointer",
          background: "none",
        }}
      />

      <Typography variant="body2" sx={{ color: "text.primary", minWidth: 120 }}>
        {label}
      </Typography>

      {key !== "primary" &&
        key !== "navBarTextColor" &&
        key !== "topNavBarTextColor" && ( // ✅ no transparent toggle for text color
          <MDButton
            variant={colorValue === "transparent" ? "contained" : "outlined"}
            size="small"
            onClick={() => handleChange(key, colorValue === "transparent" ? "#000" : "transparent")}
            sx={{
              textTransform: "none",
              fontSize: "0.75rem",
              height: 32,
              color: colorValue === "transparent" ? "#fff" : "text.secondary",
              backgroundColor: colorValue === "transparent" ? DEFAULT_PRIMARY : "transparent",
              "&:hover": {
                backgroundColor:
                  colorValue === "transparent" ? DEFAULT_PRIMARY : "rgba(0,0,0,0.04)",
              },
            }}
          >
            {colorValue === "transparent" ? t("configurator.opaque") : t("configurator.trans")}
          </MDButton>
        )}
    </MDBox>
  );

  return (
    <>
      {renderColorInput(t("configurator.primaryColor"), "primary", tempColors.primary)}
      {renderColorInput(t("configurator.navbarColor"), "navBar", tempColors.navBar)}
      {renderColorInput(
        t("configurator.topnavTextColor"),
        "topNavBarTextColor",
        tempColors.topNavBarTextColor
      )}
      {/* 🆕 */}
      {renderColorInput(t("configurator.sidenavColor"), "sideNav", tempColors.sideNav)}
      {renderColorInput(
        t("configurator.sidenavText"),
        "navBarTextColor",
        tempColors.navBarTextColor
      )}{" "}
      {renderColorInput(
        t("configurator.backgroundColor"),
        "backgroundColor",
        tempColors.backgroundColor
      )}
      <MDBox mt={1.5} display="flex" gap={1}>
        <MDButton
          variant="contained"
          color="primary"
          size="small"
          onClick={handleApply}
          sx={{ textTransform: "none" }}
        >
          {t("configurator.apply")}
        </MDButton>

        <MDButton
          variant="contained"
          size="small"
          onClick={handleReset}
          sx={{
            textTransform: "none",
            backgroundColor: DEFAULT_PRIMARY,
            color: "#fff",
            "&:hover": { backgroundColor: DEFAULT_PRIMARY },
          }}
        >
          {t("configurator.reset")}
        </MDButton>
      </MDBox>
    </>
  );
}

export default ColorPicker;
